// src/utils/Tracking.js
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from './../firebase';

// ---------------------------------------------------------------------------
//  DEBUGGING CONFIGURATION
// ---------------------------------------------------------------------------

// Set to false to disable all tracking-related console logs
const ENABLE_DEBUG_LOGS = false;

// Debug logging helper
const debugLog = (message, ...args) => {
  if (ENABLE_DEBUG_LOGS) {
    console.log(message, ...args);
  }
};

const debugError = (message, ...args) => {
  if (ENABLE_DEBUG_LOGS) {
    console.error(message, ...args);
  }
};

const debugWarn = (message, ...args) => {
  if (ENABLE_DEBUG_LOGS) {
    console.warn(message, ...args);
  }
};

// ---------------------------------------------------------------------------
//  TRACKING CONFIGURATION
// ---------------------------------------------------------------------------

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Storage keys
const STORAGE = {
  SESSION_ID: 'current_session_id',
  SESSION_START: 'session_start_time',
  LAST_EVENT_TIMESTAMP: 'last_event_timestamp',
  SESSION_DURATION: 'current_session_duration'
};

// IP detection providers in order of preference (fastest/most reliable first)
const IP_PROVIDERS = [
  'https://api.ipify.org?format=json',
  'https://ipapi.co/json/',
  'https://ip-api.com/json/'
];

// Upload queue for managing all Firestore operations
let uploadQueue = [];
let isProcessingQueue = false;

// Session creation lock to prevent race conditions
let isCreatingSession = false;
let isInitializingSession = false;

// ---------------------------------------------------------------------------
//  UPLOAD QUEUE MANAGEMENT
// ---------------------------------------------------------------------------

/**
 * Adds an upload task to the queue
 * @param {Function} uploadFunction - Function that performs the upload
 * @param {string} description - Description of the upload for logging
 * @param {number} retries - Number of retries remaining
 */
const addToUploadQueue = (uploadFunction, description, retries = 3) => {
  uploadQueue.push({
    uploadFunction,
    description,
    retries,
    timestamp: Date.now()
  });
  
  // Start processing if not already running
  if (!isProcessingQueue) {
    processUploadQueue();
  }
};

/**
 * Processes the upload queue sequentially
 */
const processUploadQueue = async () => {
  if (isProcessingQueue || uploadQueue.length === 0) return;
  
  isProcessingQueue = true;
  
  while (uploadQueue.length > 0) {
    const task = uploadQueue.shift();
    
    try {
      await task.uploadFunction();
      debugLog(`✅ Upload successful: ${task.description}`);
    } catch (error) {
      debugError(`❌ Upload failed: ${task.description}`, error);
      
      if (task.retries > 0) {
        // Retry the task
        uploadQueue.unshift({
          ...task,
          retries: task.retries - 1
        });
        debugLog(`🔄 Retrying upload: ${task.description} (${task.retries} retries left)`);
      } else {
        // Log the error as an event
        await logUploadError(task.description, error);
      }
    }
    
    // Small delay between uploads to prevent overwhelming Firestore
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  isProcessingQueue = false;
};

/**
 * Logs upload errors as events
 */
const logUploadError = async (description, error) => {
  try {
    const sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    if (!sessionId) return;
    
    const errorEvent = {
      event_timestamp: new Date().toISOString(), // Use client timestamp for events
      event_type: 'upload_error',
      event_URL: window.location.href,
      error_description: description,
      error_message: error.message || 'Unknown error',
      error_stack: error.stack || 'No stack trace'
    };
    
    const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    await updateDoc(sessionRef, {
      events: arrayUnion(errorEvent),
      last_activity_timestamp: serverTimestamp() // Only use serverTimestamp at top level
    });
    
    debugLog(`📝 Logged upload error: ${description}`);
  } catch (logError) {
    debugError('Failed to log upload error:', logError);
  }
};

// ---------------------------------------------------------------------------
//  IP AND LOCATION DETECTION
// ---------------------------------------------------------------------------

/**
 * Detects user's IP address using multiple providers
 * @returns {Promise<string>} IP address or error message
 */
const detectIP = async () => {
  for (const provider of IP_PROVIDERS) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(provider, { 
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Different providers return IP in different fields
      const ip = data.ip || data.query || 'unknown';
      if (ip && ip !== 'unknown') {
        debugLog(`✅ IP detected from ${provider}: ${ip}`);
        return ip;
      }
    } catch (error) {
      debugWarn(`❌ IP detection failed for ${provider}:`, error.message);
    }
  }
  
  return `unknown, error: All IP providers failed`;
};

/**
 * Gets location information for an IP address
 * @param {string} ip - IP address to lookup
 * @returns {Promise<string>} Country name or error message
 */
const getLocationForIP = async (ip) => {
  if (!ip || ip.startsWith('unknown')) {
    return 'unknown, error: No valid IP provided';
  }
  
  const locationProviders = [
    `https://ipapi.co/${ip}/json/`,
    `https://ip-api.com/json/${ip}`
  ];
  
  for (const provider of locationProviders) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(provider, { 
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      const country = data.country_name || data.country || 'unknown';
      if (country && country !== 'unknown') {
        debugLog(`✅ Location detected from ${provider}: ${country}`);
        return country;
      }
    } catch (error) {
      debugWarn(`❌ Location detection failed for ${provider}:`, error.message);
    }
  }
  
  return 'unknown, error: All location providers failed';
};

// ---------------------------------------------------------------------------
//  DEVICE AND BROWSER DETECTION
// ---------------------------------------------------------------------------

/**
 * Gets device information from user agent
 * @returns {Object} Device information
 */
const getDeviceInfo = () => {
  const u = navigator.userAgent;
  let type = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(u)
    ? /iPad|tablet|Tablet/i.test(u) ? 'tablet' : 'mobile'
    : 'desktop';
  let browser = 'unknown',
    os = 'unknown';

  if (/Chrome/i.test(u) && !/Chromium|Edge|OPR|Opera/i.test(u)) browser = 'Chrome';
  else if (/Firefox/i.test(u)) browser = 'Firefox';
  else if (/Safari/i.test(u) && !/Chrome|Chromium|Edge|OPR|Opera/i.test(u)) browser = 'Safari';
  else if (/Edge/i.test(u)) browser = 'Edge';
  else if (/Opera|OPR/i.test(u)) browser = 'Opera';
  else if (/MSIE|Trident/i.test(u)) browser = 'IE';

  if (/Windows/i.test(u)) os = 'Windows';
  else if (/Macintosh|Mac OS X/i.test(u)) os = 'macOS';
  else if (/iPhone|iPad|iPod/i.test(u)) os = 'iOS';
  else if (/Android/i.test(u)) os = 'Android';
  else if (/Linux/i.test(u)) os = 'Linux';

  return { type, browser, os };
};

// ---------------------------------------------------------------------------
//  TRAFFIC SOURCE ATTRIBUTION
// ---------------------------------------------------------------------------

/**
 * Extracts and returns all UTM parameters from the URL
 * @returns {Object} Object containing UTM parameters
 */
const getUtmParameters = () => {
  const url = window.location.search;
  const params = new URLSearchParams(url);
  const utmParams = {};
  
  // Extract all UTM parameters
  ['source', 'medium', 'campaign', 'term', 'content'].forEach(param => {
    const value = params.get(`utm_${param}`);
    if (value) utmParams[`utm_${param}`] = value;
  });
  
  // Extract Google click ID (gclid)
  const gclid = params.get('gclid');
  if (gclid) utmParams.gclid = gclid;
  
  // Extract Facebook click ID (fbclid)
  const fbclid = params.get('fbclid');
  if (fbclid) utmParams.fbclid = fbclid;
  
  return utmParams;
};

/**
 * Gets referrer information
 * @returns {Object} Object containing referrer data
 */
const getReferrerInfo = () => {
  const referrer = document.referrer || 'direct';
  let referrerDomain = 'direct';
  
  if (referrer !== 'direct') {
    try {
      const url = new URL(referrer);
      referrerDomain = url.hostname;
    } catch (e) {
      debugError('Error parsing referrer:', e);
    }
  }
  
  return {
    full_referrer: referrer,
    referrer_domain: referrerDomain
  };
};

/**
 * Determines the traffic source based on UTM parameters and referrer
 * @param {Object} utmParams - UTM parameters object
 * @param {Object} referrerInfo - Referrer information
 * @returns {Object} Traffic source information
 */
const determineTrafficSource = (utmParams, referrerInfo) => {
  // Check for specific ad platform identifiers
  if (utmParams.gclid) {
    return {
      source: 'google_ads',
      source_detail: utmParams.utm_campaign || 'unknown_campaign',
      attribution_type: 'paid'
    };
  }
  
  if (utmParams.fbclid) {
    return {
      source: 'meta_ads',
      source_detail: utmParams.utm_campaign || 'unknown_campaign',
      attribution_type: 'paid'
    };
  }
  
  // Check UTM source if available
  if (utmParams.utm_source) {
    let source = utmParams.utm_source.toLowerCase();
    
    // Known ad platforms
    if (source.includes('google')) source = 'google';
    if (source.includes('facebook') || source.includes('instagram') || source.includes('meta')) source = 'meta';
    
    return {
      source: source,
      source_detail: utmParams.utm_campaign || 'unknown_campaign',
      attribution_type: utmParams.utm_medium === 'organic' ? 'organic' : 'paid'
    };
  }
  
  // Check referrer domains for common sources
  const domain = referrerInfo.referrer_domain.toLowerCase();
  
  if (domain.includes('google')) {
    return {
      source: 'google',
      source_detail: 'organic_search',
      attribution_type: 'organic'
    };
  }
  
  if (domain.includes('facebook.com') || domain.includes('instagram.com')) {
    return {
      source: 'meta',
      source_detail: 'organic_social',
      attribution_type: 'organic'
    };
  }
  
  if (domain.includes('t.co') || domain.includes('twitter.com')) {
    return {
      source: 'twitter',
      source_detail: 'organic_social',
      attribution_type: 'organic'
    };
  }
  
  if (domain.includes('linkedin.com')) {
    return {
      source: 'linkedin',
      source_detail: 'organic_social',
      attribution_type: 'organic'
    };
  }
  
  // Direct or unknown
  return {
    source: referrerInfo.referrer_domain === 'direct' ? 'direct' : 'referral',
    source_detail: referrerInfo.referrer_domain === 'direct' ? 'direct' : referrerInfo.referrer_domain,
    attribution_type: 'organic'
  };
};

// ---------------------------------------------------------------------------
//  DEVELOPER DETECTION
// ---------------------------------------------------------------------------

/**
 * Checks if current user is a developer/admin
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>} True if user is a developer
 */
const checkIfDeveloper = async (userEmail) => {
  if (!userEmail) return false;
  
  try {
    const overseersRef = doc(db, 'parameters', 'overseers');
    const overseersDoc = await getDoc(overseersRef);
    
    if (overseersDoc.exists()) {
      const emails = overseersDoc.data().emails || [];
      return emails.includes(userEmail.toLowerCase());
    }
  } catch (error) {
    debugError('Error checking developer status:', error);
  }
  
  return false;
};

// ---------------------------------------------------------------------------
//  SESSION MANAGEMENT
// ---------------------------------------------------------------------------

/**
 * Generates a unique session ID
 * @returns {string} Session ID in format: YYYYMMDDHHMMSSMMM_<random>
 */
const generateSessionId = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const date = `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`;
  
  const randomPart = Math.random().toString(36).substring(2, 7);
  return `${date}_${randomPart}`;
};

/**
 * Checks if a session document exists in Firestore
 * @param {string} sessionId - The session ID to check
 * @returns {Promise<boolean>} True if the document exists
 */
const checkSessionExists = async (sessionId) => {
  if (!sessionId) return false;
  
  try {
    const docRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (err) {
    debugError('Error checking if session exists:', err);
    return false;
  }
};

/**
 * Gets the last event timestamp from a session document
 * @param {string} sessionId - The session ID
 * @returns {Promise<number|null>} Last event timestamp or null
 */
const getLastEventTimestamp = async (sessionId) => {
  if (!sessionId) return null;
  
  try {
    const docRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const events = data.events || [];
      
      if (events.length > 0) {
        // Get the most recent event timestamp
        const lastEvent = events[events.length - 1];
        
        // Handle both server timestamps and ISO string timestamps
        if (lastEvent.event_timestamp) {
          if (typeof lastEvent.event_timestamp === 'string') {
            // ISO string timestamp (new format)
            return new Date(lastEvent.event_timestamp).getTime();
          } else if (lastEvent.event_timestamp.toMillis) {
            // Server timestamp (old format)
            return lastEvent.event_timestamp.toMillis();
          }
        }
        
        return Date.now(); // Fallback to current time
      }
      
      // If no events, use document creation timestamp
      return data.timestamp?.toMillis() || Date.now();
    }
  } catch (err) {
    debugError('Error getting last event timestamp:', err);
  }
  
  return null;
};

/**
 * Checks if the session has timed out
 * @returns {Promise<boolean>} True if session has timed out
 */
const hasSessionTimedOut = async () => {
  const sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
  if (!sessionId) return true;
  
  // Check local timestamp first (faster)
  const localLastEvent = parseInt(sessionStorage.getItem(STORAGE.LAST_EVENT_TIMESTAMP) || '0', 10);
  const now = Date.now();
  
  if (now - localLastEvent > SESSION_TIMEOUT) {
    // Double-check with Firestore data
    const firestoreLastEvent = await getLastEventTimestamp(sessionId);
    if (firestoreLastEvent && now - firestoreLastEvent > SESSION_TIMEOUT) {
      return true;
    }
  }
  
  return false;
};

/**
 * Clears session data and starts fresh
 */
const clearSessionData = () => {
  sessionStorage.removeItem(STORAGE.SESSION_ID);
  sessionStorage.removeItem(STORAGE.SESSION_START);
  sessionStorage.removeItem(STORAGE.LAST_EVENT_TIMESTAMP);
  sessionStorage.removeItem(STORAGE.SESSION_DURATION);
  debugLog('🧹 Session data cleared');
  
  // Also reset initialization flags
  isCreatingSession = false;
  isInitializingSession = false;
};

/**
 * Updates session duration in sessionStorage
 */
const updateSessionDuration = () => {
  const sessionStart = parseInt(sessionStorage.getItem(STORAGE.SESSION_START) || '0', 10);
  const now = Date.now();
  const duration = Math.floor((now - sessionStart) / 1000);
  sessionStorage.setItem(STORAGE.SESSION_DURATION, duration.toString());
  return duration;
};

/**
 * Creates a new session document in Firestore
 * @param {string} sessionId - The session ID
 * @param {string} userEmail - Optional user email to check for admin status
 * @returns {Promise<boolean>} True if successful
 */
const createSessionDocument = async (sessionId, userEmail = null) => {
  // Prevent race conditions with session creation lock
  if (isCreatingSession) {
    debugLog('🔒 Session creation already in progress, waiting...');
    // Wait for existing creation to complete
    while (isCreatingSession) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Check if session was created by the other process
    const exists = await checkSessionExists(sessionId);
    return exists;
  }
  
  isCreatingSession = true;
  
  try {
    const now = Date.now();
    
    // Store session info in sessionStorage
    sessionStorage.setItem(STORAGE.SESSION_ID, sessionId);
    sessionStorage.setItem(STORAGE.SESSION_START, now.toString());
    sessionStorage.setItem(STORAGE.LAST_EVENT_TIMESTAMP, now.toString());
    sessionStorage.setItem(STORAGE.SESSION_DURATION, '0');
    
    // Start collecting data
    const device = getDeviceInfo();
    const utmParameters = getUtmParameters();
    const referrerInfo = getReferrerInfo();
    const trafficSource = determineTrafficSource(utmParameters, referrerInfo);
    
    // Check if user is a developer (for is_admin field)
    let isDeveloper = false;
    if (userEmail) {
      isDeveloper = await checkIfDeveloper(userEmail);
    }
    
    // Initial session document (without IP/country)
    const sessionData = {
      timestamp: serverTimestamp(),
      ip: 'detecting...',
      country: 'detecting...',
      device,
      entry_page_full_URL: window.location.href,
      attribution: {
        ...trafficSource,
        utm_parameters: utmParameters,
        referrer: referrerInfo.full_referrer,
        referrer_domain: referrerInfo.referrer_domain
      },
      last_activity_timestamp: serverTimestamp(),
      total_session_duration: 0,
      events: []
    };
    
    // Add is_admin field only if user is a developer
    if (isDeveloper) {
      sessionData.is_admin = true;
    }
    
    // Ensure parent document exists
    await setDoc(doc(db, 'tracking', 'sessions'), { last_updated: serverTimestamp() }, { merge: true });
    
    // Create session document
    const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    await setDoc(sessionRef, sessionData);
    
    debugLog(`✅ Session document created: ${sessionId}${isDeveloper ? ' (Admin)' : ''}`);
    
    // Start IP detection process
    startIPDetectionProcess(sessionId);
    
    return true;
  } catch (error) {
    debugError('Error creating session document:', error);
    return false;
  } finally {
    isCreatingSession = false;
  }
};

/**
 * Starts the IP detection process
 * @param {string} sessionId - The session ID
 */
const startIPDetectionProcess = async (sessionId) => {
  // First, detect and upload IP immediately
  const ip = await detectIP();
  
  const uploadIP = async () => {
    const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    await updateDoc(sessionRef, { ip });
  };
  
  addToUploadQueue(uploadIP, `IP update for session ${sessionId}`);
  
  // Then get location information
  const country = await getLocationForIP(ip);
  
  const uploadCountry = async () => {
    const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
    await updateDoc(sessionRef, { country });
  };
  
  addToUploadQueue(uploadCountry, `Country update for session ${sessionId}`);
};

// ---------------------------------------------------------------------------
//  SESSION INITIALIZATION
// ---------------------------------------------------------------------------

/**
 * Initializes or continues a tracking session
 * @param {string} userEmail - Optional user email to check for admin status
 * @returns {Promise<string|null>} Session ID or null if failed
 */
export const initializeSession = async (userEmail = null) => {
  // Prevent race conditions with initialization lock
  if (isInitializingSession) {
    debugLog('🔒 Session initialization already in progress, waiting...');
    // Wait for existing initialization to complete
    while (isInitializingSession) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Return the session ID that was created by the other process
    return sessionStorage.getItem(STORAGE.SESSION_ID);
  }
  
  isInitializingSession = true;
  
  try {
    // Step 1: Check if we have a session ID
    let sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    
    // Step 2: If no session ID, create one
    if (!sessionId) {
      sessionId = generateSessionId();
      debugLog(`🆕 Creating new session: ${sessionId}`);
      
      const success = await createSessionDocument(sessionId, userEmail);
      if (!success) {
        debugError('Failed to create session document');
        return null;
      }
      
      return sessionId;
    }
    
    // Step 3: We have a session ID, check if it exists in Firestore
    debugLog(`🔍 Checking existing session: ${sessionId}`);
    const exists = await checkSessionExists(sessionId);
    
    if (!exists) {
      // Session doesn't exist, create it
      debugLog(`📄 Session document doesn't exist, creating: ${sessionId}`);
      const success = await createSessionDocument(sessionId, userEmail);
      if (!success) {
        clearSessionData();
        return null;
      }
      return sessionId;
    }
    
    // Step 4: Check if session has timed out
    const timedOut = await hasSessionTimedOut();
    
    if (timedOut) {
      debugLog(`⏰ Session timed out, creating new session`);
      clearSessionData();
      
      // Create new session
      sessionId = generateSessionId();
      const success = await createSessionDocument(sessionId, userEmail);
      if (!success) {
        debugError('Failed to create new session after timeout');
        return null;
      }
      
      return sessionId;
    }
    
    // Step 5: Session is valid, continue with it
    debugLog(`✅ Continuing with existing session: ${sessionId}`);
    return sessionId;
    
  } catch (error) {
    debugError('Error initializing session:', error);
    clearSessionData();
    return null;
  } finally {
    isInitializingSession = false;
  }
};

// ---------------------------------------------------------------------------
//  EVENT TRACKING
// ---------------------------------------------------------------------------

/**
 * Adds an event to the current session
 * @param {string} eventType - Type of event
 * @param {Object} additionalData - Additional event data
 * @returns {Promise<boolean>} True if successful
 */
export const trackEvent = async (eventType, additionalData = {}) => {
  try {
    // Ensure we have an active session
    let sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    
    if (!sessionId) {
      sessionId = await initializeSession();
      if (!sessionId) {
        debugError('Failed to initialize session for event tracking');
        return false;
      }
    }
    
    // Check if session has timed out
    const timedOut = await hasSessionTimedOut();
    if (timedOut) {
      debugLog(`⏰ Session timed out during event, creating new session`);
      sessionId = await initializeSession();
      if (!sessionId) {
        debugError('Failed to create new session after timeout');
        return false;
      }
    }
    
    const now = Date.now();
    
    // Update local session data
    sessionStorage.setItem(STORAGE.LAST_EVENT_TIMESTAMP, now.toString());
    const duration = updateSessionDuration();
    
    // Create event object with client-side timestamp
    const eventData = {
      event_timestamp: new Date().toISOString(), // Use client timestamp for events
      event_type: eventType,
      event_URL: window.location.href,
      ...additionalData
    };
    
    // Add to upload queue
    const uploadEvent = async () => {
      const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
      await updateDoc(sessionRef, {
        events: arrayUnion(eventData),
        last_activity_timestamp: serverTimestamp(), // Only use serverTimestamp at top level
        total_session_duration: duration
      });
    };
    
    addToUploadQueue(uploadEvent, `${eventType} event for session ${sessionId}`);
    
    debugLog(`📝 Event tracked: ${eventType}`);
    return true;
    
  } catch (error) {
    debugError('Error tracking event:', error);
    return false;
  }
};

/**
 * Updates session with admin status if user is a developer
 * @param {string} userEmail - User's email address
 */
const updateSessionAdminStatus = async (userEmail) => {
  if (!userEmail) return;
  
  const sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
  if (!sessionId) return;
  
  try {
    const isDeveloper = await checkIfDeveloper(userEmail);
    if (isDeveloper) {
      const uploadAdminStatus = async () => {
        const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
        await updateDoc(sessionRef, {
          is_admin: true,
          last_activity_timestamp: serverTimestamp()
        });
      };
      
      addToUploadQueue(uploadAdminStatus, `Admin status update for session ${sessionId}`);
      debugLog(`👑 Admin status added to session: ${sessionId}`);
    }
  } catch (error) {
    debugError('Error updating admin status:', error);
  }
};

/**
 * Tracks user authentication
 * @param {Object} user - User object with email and uid
 * @param {string} authMethod - Authentication method
 */
export const trackUserAuthentication = async (user, authMethod = 'unknown') => {
  const additionalData = {
    user_id: user.uid,
    user_email: user.email || 'unknown',
    auth_method: authMethod
  };
  
  // Track the authentication event
  const success = await trackEvent('user_authenticated', additionalData);
  
  // Update session with admin status if user is a developer
  if (success && user.email) {
    await updateSessionAdminStatus(user.email);
  }
  
  return success;
};

/**
 * Tracks URL changes
 * @param {string} fromURL - Previous URL
 * @param {string} toURL - New URL
 */
export const trackURLChange = async (fromURL, toURL) => {
  return await trackEvent('URL_change', {
    from_URL: fromURL,
    to_URL: toURL
  });
};

/**
 * Tracks campaign detection
 * @param {Object} campaignData - Campaign data from URL
 */
export const trackCampaignDetection = async (campaignData) => {
  return await trackEvent('campaign_detected', {
    ...campaignData,
    detection_URL: window.location.href
  });
};

// ---------------------------------------------------------------------------
//  BASIC USER ACTIVITY TRACKING
// ---------------------------------------------------------------------------

let lastActivityTime = 0;
const ACTIVITY_THROTTLE = 10000; // 10 seconds between activity pings
let activityTimeout = null;

/**
 * Tracks basic user activity (scroll, click, etc.)
 * Throttled to prevent excessive events
 */
const trackActivity = async (activityType) => {
  const now = Date.now();
  if (now - lastActivityTime < ACTIVITY_THROTTLE) return;
  
  lastActivityTime = now;
  await trackEvent(activityType);
};

/**
 * Sets up basic activity tracking
 */
export const setupActivityTracking = () => {
  // Clear any existing timeout
  if (activityTimeout) {
    clearTimeout(activityTimeout);
  }
  
  // Scroll tracking
  let scrollTimeout;
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => trackActivity('scroll'), 500);
  };
  
  // Click tracking
  const handleClick = () => trackActivity('click');
  
  // Keyboard tracking
  const handleKeydown = () => trackActivity('keydown');
  
  // Add event listeners
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('click', handleClick, { passive: true });
  window.addEventListener('keydown', handleKeydown, { passive: true });
  
  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('click', handleClick);
    window.removeEventListener('keydown', handleKeydown);
    if (scrollTimeout) clearTimeout(scrollTimeout);
  };
};

// ---------------------------------------------------------------------------
//  URL CHANGE TRACKING
// ---------------------------------------------------------------------------

let currentURL = window.location.href;
let urlCheckInterval = null;

/**
 * Sets up URL change tracking
 */
export const setupURLTracking = () => {
  // Clear any existing interval
  if (urlCheckInterval) {
    clearInterval(urlCheckInterval);
  }
  
  const checkURLChange = () => {
    const newURL = window.location.href;
    if (newURL !== currentURL) {
      trackURLChange(currentURL, newURL);
      currentURL = newURL;
    }
  };
  
  // Check for URL changes every second
  urlCheckInterval = setInterval(checkURLChange, 1000);
  
  // Also listen for popstate events (back/forward navigation)
  const handlePopState = () => {
    setTimeout(checkURLChange, 100);
  };
  
  window.addEventListener('popstate', handlePopState);
  
  // Return cleanup function
  return () => {
    if (urlCheckInterval) {
      clearInterval(urlCheckInterval);
      urlCheckInterval = null;
    }
    window.removeEventListener('popstate', handlePopState);
  };
};

// ---------------------------------------------------------------------------
//  ONBOARDING LINKING HELPERS
// ---------------------------------------------------------------------------

/**
 * Links the current tracking session to an onboarding ID by storing
 * onboarding_id at the session document top-level.
 * Also emits a lightweight event for auditability.
 * @param {string} onboardingId
 * @returns {Promise<boolean>} True if successfully queued
 */
export const linkOnboardingToSession = async (onboardingId) => {
  try {
    if (!onboardingId) return false;
    // Ensure we have an active session
    let sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    if (!sessionId) {
      sessionId = await initializeSession();
      if (!sessionId) return false;
    }

    const upload = async () => {
      const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
      await updateDoc(sessionRef, {
        onboarding_id: onboardingId,
        last_activity_timestamp: serverTimestamp(),
      });
    };

    addToUploadQueue(upload, `Link onboarding ${onboardingId} to session ${sessionId}`);

    // Also record an event that the link was set (idempotent on server side is OK)
    await trackEvent('onboarding_linked_to_session', {
      onboarding_id: onboardingId,
    });

    return true;
  } catch (error) {
    debugError('Error linking onboarding to session:', error);
    return false;
  }
};

/**
 * Updates top-level max step reached fields on the tracking session document.
 * This is not recorded as an event; it is a session-level checkpoint.
 * @param {{onboardingId:string, stepIndex:number, stepKey?:string, stepLabel?:string}} payload
 */
export const updateOnboardingMaxStep = async (payload) => {
  try {
    const { onboardingId, stepIndex, stepKey, stepLabel } = payload || {};
    if (typeof stepIndex !== 'number') return false;

    // Ensure we have an active session
    let sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    if (!sessionId) {
      sessionId = await initializeSession();
      if (!sessionId) return false;
    }

    const upload = async () => {
      const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
      const patch = {
        onboarding_max_step_reached_index: stepIndex,
        last_activity_timestamp: serverTimestamp(),
      };
      if (onboardingId) patch.onboarding_id = onboardingId;
      if (stepKey) patch.onboarding_max_step_reached_key = stepKey;
      if (stepLabel) patch.onboarding_max_step_reached_label = stepLabel;
      await updateDoc(sessionRef, patch);
    };

    addToUploadQueue(upload, `Update onboarding max step ${stepIndex} for session`);
    return true;
  } catch (error) {
    debugError('Error updating onboarding max step:', error);
    return false;
  }
};

// ---------------------------------------------------------------------------
//  VIEW REPORT LINKING HELPERS
// ---------------------------------------------------------------------------

/**
 * Links the current tracking session to a view-report ID by storing
 * view_report_id at the session document top-level.
 * Also emits a lightweight event for auditability.
 * @param {string} viewReportId - The view report session ID
 * @param {string} [reportRequestId] - The original report request ID from URL
 * @returns {Promise<boolean>} True if successfully queued
 */
export const linkViewReportToSession = async (viewReportId, reportRequestId = null) => {
  try {
    if (!viewReportId) return false;
    // Ensure we have an active session
    let sessionId = sessionStorage.getItem(STORAGE.SESSION_ID);
    if (!sessionId) {
      sessionId = await initializeSession();
      if (!sessionId) return false;
    }

    const upload = async () => {
      const sessionRef = doc(db, 'tracking', 'sessions', 'data', sessionId);
      const patch = {
        view_report_id: viewReportId,
        last_activity_timestamp: serverTimestamp(),
      };
      if (reportRequestId) {
        patch.view_report_request_id = reportRequestId;
      }
      await updateDoc(sessionRef, patch);
    };

    addToUploadQueue(upload, `Link view_report ${viewReportId} to session ${sessionId}`);

    // Also record an event that the link was set
    await trackEvent('view_report_linked_to_session', {
      view_report_id: viewReportId,
      report_request_id: reportRequestId,
    });

    return true;
  } catch (error) {
    debugError('Error linking view_report to session:', error);
    return false;
  }
};

/**
 * Track view report page view event.
 * @param {{viewReportId:string, reportRequestId?:string, pageName:string, tabName?:string}} payload
 * @returns {Promise<boolean>}
 */
export const trackViewReportPageView = async (payload) => {
  try {
    const { viewReportId, reportRequestId, pageName, tabName } = payload || {};
    if (!pageName) return false;

    return await trackEvent('view_report_page_view', {
      view_report_id: viewReportId || null,
      report_request_id: reportRequestId || null,
      page_name: pageName,
      tab_name: tabName || null,
    });
  } catch (error) {
    debugError('Error tracking view report page view:', error);
    return false;
  }
};

/**
 * Track view report API call result.
 * @param {{viewReportId:string, reportRequestId?:string, reportType:string, success:boolean, error?:string, statusCode?:number}} payload
 * @returns {Promise<boolean>}
 */
export const trackViewReportApiCall = async (payload) => {
  try {
    const { viewReportId, reportRequestId, reportType, success, error, statusCode } = payload || {};

    return await trackEvent('view_report_api_call', {
      view_report_id: viewReportId || null,
      report_request_id: reportRequestId || null,
      report_type: reportType || null,
      success: Boolean(success),
      error_message: error || null,
      status_code: statusCode || null,
    });
  } catch (error) {
    debugError('Error tracking view report API call:', error);
    return false;
  }
};

/**
 * Track contact info submission from view report page.
 * @param {{viewReportId:string, reportRequestId?:string, contactType:string, phoneNumber?:string, dialCode?:string, phoneRegion?:string, emailAddress?:string, source:string}} payload
 * @returns {Promise<boolean>}
 */
export const trackViewReportContactSubmission = async (payload) => {
  try {
    const { 
      viewReportId, 
      reportRequestId, 
      contactType, 
      phoneNumber, 
      dialCode, 
      phoneRegion, 
      emailAddress,
      source 
    } = payload || {};

    const eventData = {
      view_report_id: viewReportId || null,
      report_request_id: reportRequestId || null,
      contact_type: contactType || null,
      source: source || 'view_report',
    };

    if (contactType === 'phone' && phoneNumber) {
      eventData.phone_number = phoneNumber;
      eventData.dial_code = dialCode || null;
      eventData.phone_region = phoneRegion || null;
    }

    if (contactType === 'email' && emailAddress) {
      eventData.email_address = emailAddress;
    }

    // CRITICAL: Always log contact submissions regardless of debug mode
    console.log('[Tracking] 📞📧 CONTACT SUBMISSION EVENT:', {
      contactType,
      value: contactType === 'phone' ? phoneNumber : emailAddress,
      viewReportId,
      reportRequestId,
      source
    });

    const result = await trackEvent('view_report_contact_submission', eventData);
    
    // CRITICAL: Log success/failure
    if (result) {
      console.log('[Tracking] ✅ CONTACT SUBMISSION TRACKED SUCCESSFULLY');
    } else {
      console.error('[Tracking] ❌ CONTACT SUBMISSION TRACKING FAILED');
    }
    
    return result;
  } catch (error) {
    // CRITICAL: Always log contact submission errors
    console.error('[Tracking] ❌ CONTACT SUBMISSION ERROR:', error);
    debugError('Error tracking view report contact submission:', error);
    return false;
  }
};

/**
 * Track new chat button click (WhatsApp, Messenger, iMessage, etc.).
 * Used by ViewReport system and ContactInfoPage.
 * @param {{viewReportId:string, reportRequestId?:string, buttonType:string, href:string, pageName:string}} payload
 * @returns {Promise<boolean>}
 */
export const trackViewReportContactButtonClick = async (payload) => {
  try {
    const { viewReportId, reportRequestId, buttonType, href, pageName } = payload || {};

    return await trackEvent('new_chat_button_clicked', {
      // Core button info
      button_type: buttonType || null,
      href: href || null,
      // Source info
      source_type: 'view_report',
      source_detail: pageName || null,
      // View report specific
      view_report_id: viewReportId || null,
      report_request_id: reportRequestId || null,
    });
  } catch (error) {
    debugError('Error tracking new chat button click:', error);
    return false;
  }
};

// ---------------------------------------------------------------------------
//  PUBLIC API (must be at end - after all functions are defined)
// ---------------------------------------------------------------------------

export default {
  initializeSession,
  trackEvent,
  trackUserAuthentication,
  trackURLChange,
  trackCampaignDetection,
  setupActivityTracking,
  setupURLTracking,
  linkOnboardingToSession,
  updateOnboardingMaxStep,
  linkViewReportToSession,
  trackViewReportPageView,
  trackViewReportApiCall,
  trackViewReportContactSubmission,
  trackViewReportContactButtonClick
};